import {
  cmToInches,
  createPptx,
  formatNumberWithSuffix,
  generateSourceText,
  getSourceTextBox,
  getMetricValueShift,
  getMetricGroupBounds,
  getImageUrl,
  hasData,
  inchesToCm,
  layoutMetricGroupsByBoundsRightToLeft,
  TRANSLATIONS,
} from "./utils";

function mapBrandToTemplateData(brand, lang = "fr") {
  const report = brand.report || {};
  return {
    id: brand.id,
    author: brand.author || "",
    job: (lang === 'en' && brand.author_occupation_en) ? brand.author_occupation_en : (brand.author_occupation || ""),
    title: (lang === 'en' && brand.title_en) ? brand.title_en : (brand.title || ""),
    baseline: (lang === 'en' && brand.baseline_en) ? brand.baseline_en : (brand.baseline || ""),
    target: (lang === 'en' && brand.positioning_en) ? brand.positioning_en : (brand.positioning || ""),
    author_image_path: brand.author_image_path,
    presentation_image_path: brand.presentation_image_path,
    social_image_path: brand.social_image_path,
    audience_brand: report.audience_brand || 0,
    print_readers: report.audience_print_readers || 0,
    print_copies: report.diffusion_print || 0,
    digital_visitors: report.audience_digital || 0,
    social_followers: report.social_data || 0,
    audio_listeners: report.audio_data || 0,
    newsletter_subscribers: report.newsletter_data || 0,
  };
}

export async function generateTemplate1(brand, sourceSettings = {}, lang = "fr") {
  const data = mapBrandToTemplateData(brand, lang);
  const pptx = createPptx(brand.brand_name);
  const slide = pptx.addSlide();
  const reportData = brand.report || {};
  const sourceText = generateSourceText(1, sourceSettings, reportData, lang);
  const t = TRANSLATIONS[lang];
  const pageWidthCm = inchesToCm(13.333);
  const metricLayoutConfig = {
    zoneRightCm: pageWidthCm - 0.5,
    gapCm: 0.11,
    separatorGapCm: 0.51,
  };

  const bgUrl = getImageUrl("marketing/orange_bg.png");

  slide.addImage({
    path: bgUrl,
    x: cmToInches(7.16),
    y: cmToInches(-2.26),
    w: cmToInches(21.81),
    h: cmToInches(23),
    rotate: 353,
  });

  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: 13.33,
    h: 7.5,
    fill: { type: "solid", color: "FFFFFF", transparency: 25 },
    line: { type: "none" },
  });

  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: 13.33,
    h: 7.5,
    fill: { type: "none" },
    line: {
      color: "D77423",
      width: 8,
    },
  });

  // add reworld media connect logo
  slide.addImage({
    path: getImageUrl("marketing/reworld_media_connect_logo.png"),
    h: cmToInches(0.72),
    w: cmToInches(2.93),
    x: cmToInches(0.56),
    y: cmToInches(17.83),
  });

  const { number: audienceBrandNum, suffix: audienceBrandSuffix } =
    formatNumberWithSuffix(data.audience_brand, lang);
  const { number: printReadersNum, suffix: printReadersSuffix } =
    formatNumberWithSuffix(data.print_readers, lang);
  const { number: printCopiesNum, suffix: printCopiesSuffix } =
    formatNumberWithSuffix(data.print_copies, lang);
  const { number: digitalVisitorsNum, suffix: digitalVisitorsSuffix } =
    formatNumberWithSuffix(data.digital_visitors, lang);
  const { number: socialFollowersNum, suffix: socialFollowersSuffix } =
    formatNumberWithSuffix(data.social_followers, lang);
  const { number: audioListenersNum, suffix: audioListenersSuffix } =
    formatNumberWithSuffix(data.audio_listeners, lang);
  const {
    number: newsletterSubscribersNum,
    suffix: newsletterSubscribersSuffix,
  } = formatNumberWithSuffix(data.newsletter_subscribers, lang);
  const socialValueShiftCm = getMetricValueShift("template1", "social", socialFollowersNum);
  const hasPrintCopies = hasData(data.print_copies);
  const hasPrintReaders = hasData(data.print_readers);
  const isSinglePrintRow = hasPrintCopies !== hasPrintReaders;
  const hasNewsletter = hasData(data.newsletter_subscribers);
  const hasPodcast = hasData(data.audio_listeners);
  const isSingleNewsletterPodcastRow = hasNewsletter !== hasPodcast;

  const fontName = "Montserrat, Arial";
  const orangeColor = "D77423";

  const renderMetricSeparator = (group) => {
    if (group.separatorXCm === null) {
      return;
    }

    slide.addShape("line", {
      x: cmToInches(group.separatorXCm),
      y: cmToInches(group.separatorY),
      w: cmToInches(0),
      h: cmToInches(group.separatorHCm),
      line: {
        color: "#000000",
        width: 0.75,
      },
    });
  };

  const renderSingleMetricGroup = (group) => {
    slide.addText(
      [
        {
          text: group.title,
          options: {
            fontSize: 18,
            bold: true,
            color: "000000",
            fontFace: fontName,
            align: "center",
          },
        },
      ],
      {
        h: cmToInches(group.titleBox.hCm),
        w: cmToInches(group.titleBox.wCm),
        x: cmToInches(group.anchorXCm + group.titleBox.offsetXCm),
        y: cmToInches(group.titleBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    slide.addText(
      [
        {
          text: group.number,
          options: {
            fontSize: 36,
            bold: true,
            color: "000000",
            fontFace: fontName,
            align: "right",
          },
        },
      ],
      {
        h: cmToInches(group.numberBox.hCm),
        w: cmToInches(group.numberBox.wCm),
        x: cmToInches(group.anchorXCm + group.numberBox.offsetXCm),
        y: cmToInches(group.numberBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    if (group.suffix) {
      slide.addText(
        [
          {
            text: group.suffix,
            options: {
              fontSize: 18,
              bold: true,
              color: "000000",
              fontFace: fontName,
              align: "left",
            },
          },
        ],
        {
          h: cmToInches(group.suffixBox.hCm),
          w: cmToInches(group.suffixBox.wCm),
          x: cmToInches(group.anchorXCm + group.suffixBox.offsetXCm),
          y: cmToInches(group.suffixBox.yCm),
          valign: "top",
          halign: "left",
        }
      );
    }

    slide.addText(
      [
        {
          text: group.unit,
          options: {
            fontSize: 10.5,
            bold: false,
            color: "000000",
            fontFace: fontName,
            align: "left",
          },
        },
      ],
      {
        h: cmToInches(group.unitBox.hCm),
        w: cmToInches(group.unitBox.wCm),
        x: cmToInches(group.anchorXCm + group.unitBox.offsetXCm),
        y: cmToInches(group.unitBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    renderMetricSeparator(group);
  };

  const renderStackedMetricGroup = (group) => {
    slide.addText(
      [
        {
          text: group.title,
          options: {
            fontSize: 18,
            bold: true,
            color: "000000",
            fontFace: fontName,
            align: "center",
          },
        },
      ],
      {
        h: cmToInches(group.titleBox.hCm),
        w: cmToInches(group.titleBox.wCm),
        x: cmToInches(group.anchorXCm + group.titleBox.offsetXCm),
        y: cmToInches(group.titleBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    group.rows.filter((row) => row.visible).forEach((row) => {
      slide.addText(
        [
          {
            text: row.number,
            options: {
              fontSize: 36,
              bold: true,
              color: "000000",
              fontFace: fontName,
              align: "right",
            },
          },
        ],
        {
          h: cmToInches(row.numberBox.hCm),
          w: cmToInches(row.numberBox.wCm),
          x: cmToInches(group.anchorXCm + row.numberBox.offsetXCm),
          y: cmToInches(row.numberBox.yCm),
          valign: "top",
          halign: "left",
        }
      );

      if (row.suffix) {
        slide.addText(
          [
            {
              text: row.suffix,
              options: {
                fontSize: 18,
                bold: true,
                color: "000000",
                fontFace: fontName,
                align: "left",
              },
            },
          ],
          {
            h: cmToInches(row.suffixBox.hCm),
            w: cmToInches(row.suffixBox.wCm),
            x: cmToInches(group.anchorXCm + row.suffixBox.offsetXCm),
            y: cmToInches(row.suffixBox.yCm),
            valign: "top",
            halign: "left",
          }
        );
      }

      slide.addText(
        [
          {
            text: row.unit,
            options: {
              fontSize: 10.5,
              bold: false,
              color: "000000",
              fontFace: fontName,
              align: "left",
            },
          },
        ],
        {
          h: cmToInches(row.unitBox.hCm),
          w: cmToInches(row.unitBox.wCm),
          x: cmToInches(group.anchorXCm + row.unitBox.offsetXCm),
          y: cmToInches(row.unitBox.yCm),
          valign: "top",
          halign: "left",
        }
      );
    });

    renderMetricSeparator(group);
  };
  // author image
  if (data.author_image_path) {
    slide.addImage({
      path: getImageUrl(data.author_image_path),
      x: cmToInches(17.42),
      y: cmToInches(1.24),
      w: cmToInches(2.52),
      h: cmToInches(2.52),
    });
  }

  // presentation image
  slide.addImage({
    path: getImageUrl(data.presentation_image_path),
    h: cmToInches(10.87),
    w: cmToInches(8.29),
    x: cmToInches(1.29),
    y: cmToInches(1.16),
  });

  // social image
  slide.addImage({
    path: getImageUrl(data.social_image_path),
    h: cmToInches(11.17),
    w: cmToInches(5.41),
    x: cmToInches(11.03),
    y: cmToInches(0.96),
  });

  // source
  slide.addText(
    [
      {
        text: sourceText,
        options: {
          fontSize: 8,
          bold: false,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    getSourceTextBox()
  );

  slide.addText(
    [
      {
        text: data.author,
        options: {
          fontSize: 24,
          bold: true,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      h: cmToInches(0.9),
      w: cmToInches(15.29),
      x: cmToInches(20.47),
      y: cmToInches(1.47),
      valign: "top",
      halign: "left",
    }
  );

  slide.addText(
    [
      {
        text: data.job,
        options: {
          fontSize: 18,
          bold: false,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(20.56),
      y: cmToInches(2.48),
      w: cmToInches(10.97),
      h: cmToInches(1.53),
      valign: "top",
      halign: "left",
    }
  );

  // title
  slide.addText(
    [
      {
        text: data.title,
        options: {
          fontSize: 16,
          bold: true,
          color: "ffffff",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(17.39),
      y: cmToInches(4.64),
      w: cmToInches(13.66),
      h: cmToInches(1),
      highlight: orangeColor,
      valign: "top",
      halign: "left",
    }
  );

  // baseline
  slide.addText(
    [
      {
        text: data.baseline,
        options: {
          fontSize: 12,
          bold: true,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(17.4),
      y: cmToInches(6.35),
      w: cmToInches(15),
      h: cmToInches(1),
      valign: "top",
      halign: "left",
    }
  );

  // target audience
  slide.addText(
    [
      {
        text: data.target,
        options: {
          fontSize: 12,
          bold: false,
          color: "000000",
          fontFace: fontName,
          lineSpacing: 12,
        },
      },
    ],
    {
      x: cmToInches(17.4),
      y: cmToInches(7.2),
      w: cmToInches(14.37),
      h: cmToInches(4.87),
      valign: "top",
      halign: "left",
    }
  );

  const printRowDefinitions = [
    {
      id: "copies",
      visible: hasPrintCopies,
      number: printCopiesNum,
      suffix: printCopiesSuffix,
      unit: t.EXEMPLAIRES,
      numberBox: { offsetXCm: -1.85, yCm: isSinglePrintRow ? 15.17 : 14.13, wCm: 4.48, hCm: 1.8 },
      suffixBox: { offsetXCm: 2.35, yCm: isSinglePrintRow ? 15.31 : 14.32, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 2.35, yCm: isSinglePrintRow ? 15.96 : 14.92, wCm: 4.44, hCm: 0.71 },
    },
    {
      id: "readers",
      visible: hasPrintReaders,
      number: printReadersNum,
      suffix: printReadersSuffix,
      unit: t.LECTEURS,
      numberBox: {
        offsetXCm: -1.85,
        yCm: hasPrintCopies ? 15.71 : 15.17,
        wCm: 4.48,
        hCm: 1.8,
      },
      suffixBox: {
        offsetXCm: 2.35,
        yCm: hasPrintCopies ? 15.91 : 15.31,
        wCm: 2.05,
        hCm: 1.03,
      },
      unitBox: {
        offsetXCm: 2.35,
        yCm: hasPrintCopies ? 16.5 : 15.96,
        wCm: 4.44,
        hCm: 0.71,
      },
    },
  ];

  const newsletterPodcastRows = [
    {
      id: "newsletter",
      visible: hasNewsletter,
      number: newsletterSubscribersNum,
      suffix: newsletterSubscribersSuffix,
      unit: t.ABONNES_NEWSLETTER,
      numberBox: { offsetXCm: -1.82, yCm: isSingleNewsletterPodcastRow ? 15.17 : 14.29, wCm: 4.48, hCm: 1.8 },
      suffixBox: { offsetXCm: 2.55, yCm: isSingleNewsletterPodcastRow ? 15.31 : 14.43, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 2.58, yCm: isSingleNewsletterPodcastRow ? 15.96 : 15.08, wCm: 3.37, hCm: 1.6 },
    },
    {
      id: "podcast",
      visible: hasPodcast,
      number: audioListenersNum,
      suffix: audioListenersSuffix,
      unit: t.PODCAST_IMPRESSIONS,
      numberBox: { offsetXCm: -2.31, yCm: hasNewsletter ? 16.21 : 15.17, wCm: 5.23, hCm: 1.8 },
      suffixBox: { offsetXCm: 2.64, yCm: hasNewsletter ? 16.33 : 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 2.64, yCm: hasNewsletter ? 17 : 15.96, wCm: 3.37, hCm: 1.15 },
    },
  ];

  const newsletterPodcastTitle =
    hasData(data.newsletter_subscribers) && hasData(data.audio_listeners)
      ? "NEWSLETTER & PODCAST"
      : hasData(data.newsletter_subscribers)
        ? "NEWSLETTER"
        : "PODCAST";

  const metricTitleYCm = 13.11;
  const metricTitleLineHeightCm = 0.76;
  const isTwoLineNewsletterPodcastTitle = hasNewsletter && hasPodcast;
  const newsletterPodcastTitleYCm = isTwoLineNewsletterPodcastTitle
    ? metricTitleYCm - metricTitleLineHeightCm / 2
    : metricTitleYCm;

  const metricGroupDefinitions = [
    {
      id: "newsletter_podcast",
      type: "stacked",
      visible:
        hasData(data.newsletter_subscribers) || hasData(data.audio_listeners),
      title: newsletterPodcastTitle,
      titleBox: {
        offsetXCm: 0,
        yCm: newsletterPodcastTitleYCm,
        wCm: 5.11,
        hCm: 1.5,
      },
      rows: newsletterPodcastRows,
      rightMarginCm: 0.6,
      leftNeighborAnchorStepCm: 6.75,
      separatorOffsetXCm: -1.05,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
    {
      id: "social",
      type: "single",
      visible: hasData(data.social_followers),
      title: "SOCIAL",
      number: socialFollowersNum,
      suffix: socialFollowersSuffix,
      unit: t.ABONNES_RS,
      titleBox: { offsetXCm: 0, yCm: 13.11, wCm: 5.11, hCm: 1.03 },
      numberBox: { offsetXCm: -1.15 + socialValueShiftCm, yCm: 15.17, wCm: 3.59, hCm: 1.8 },
      suffixBox: { offsetXCm: 2.13 + socialValueShiftCm, yCm: 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 2.14 + socialValueShiftCm, yCm: 15.96, wCm: 4.44, hCm: 0.71 },
      leftNeighborAnchorStepCm: 5,
      separatorOffsetXCm: -0.41,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
    {
      id: "digital",
      type: "single",
      visible: hasData(data.digital_visitors),
      title: "DIGITAL",
      number: digitalVisitorsNum,
      suffix: digitalVisitorsSuffix,
      unit: t.VU,
      titleBox: { offsetXCm: 0, yCm: 13.11, wCm: 5.11, hCm: 1.03 },
      numberBox: { offsetXCm: -0.9, yCm: 15.17, wCm: 3.95, hCm: 1.8 },
      suffixBox: { offsetXCm: 2.88, yCm: 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 2.9, yCm: 15.96, wCm: 4.44, hCm: 1.15 },
      leftNeighborAnchorStepCm: 5.94,
      separatorOffsetXCm: 0.2,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
    {
      id: "print",
      type: "stacked",
      visible: printRowDefinitions.some((row) => row.visible),
      title: "PRINT",
      titleBox: { offsetXCm: 0, yCm: 13.11, wCm: 5.11, hCm: 1.03 },
      rows: printRowDefinitions,
      leftNeighborAnchorStepCm: 8.03,
      separatorOffsetXCm: -1.25,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
    {
      id: "brand",
      type: "single",
      visible: hasData(data.audience_brand),
      title: "BRAND",
      number: audienceBrandNum,
      suffix: audienceBrandSuffix,
      unit: t.FRANCAIS_MOIS,
      titleBox: { offsetXCm: 0, yCm: 13.11, wCm: 6.84, hCm: 1.03 },
      numberBox: { offsetXCm: -1.45, yCm: 15.17, wCm: 4.12, hCm: 1.8 },
      suffixBox: { offsetXCm: 2.41, yCm: 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 2.39, yCm: 15.96, wCm: 4.44, hCm: 0.71 },
      separatorOffsetXCm: 6.78,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
  ].map((group) => {
    if (group.type === "stacked") {
      return {
        ...group,
        bounds: getMetricGroupBounds([
          group.titleBox,
          ...group.rows
            .filter((row) => row.visible)
            .flatMap((row) => [
              row.numberBox,
              ...(row.suffix ? [row.suffixBox] : []),
              row.unitBox,
            ]),
        ]),
      };
    }

    return {
      ...group,
      bounds: getMetricGroupBounds([
        group.titleBox,
        group.numberBox,
        ...(group.suffix ? [group.suffixBox] : []),
        group.unitBox,
      ]),
    };
  });

  const metricGroups = layoutMetricGroupsByBoundsRightToLeft(
    metricGroupDefinitions,
    metricLayoutConfig
  );

  metricGroups.forEach((group) => {
    if (group.type === "stacked") {
      renderStackedMetricGroup(group);
      return;
    }

    renderSingleMetricGroup(group);
  });

  return pptx;
}
